import { createRoot } from "react-dom/client";
import PointTemplate1 from "@/pages/loyalty/templets/card/points/tempate1";
import PointTemplate2 from "@/pages/loyalty/templets/card/points/template2";
import PointTemplate3 from "@/pages/loyalty/templets/card/points/template3";
import PointTemplate4 from "@/pages/loyalty/templets/card/points/template4";
import {
  getComponentByTemplate,
  getTemplateForRecord,
  sortTemplatesBySerial,
} from "@/lib/loyalty-template-utils";
import {
  inlineAssetsForCapture,
  waitForFonts,
  waitForImages,
  waitForRender,
} from "@/lib/export-template-utils";

const POINT_TEMPLATE_COMPONENTS = [
  PointTemplate1,
  PointTemplate2,
  PointTemplate3,
  PointTemplate4,
];

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB");
};

const formatReward = (reward, rewardType) => {
  if (reward === null || reward === undefined || reward === "") return null;

  if (rewardType === "percentage") return `${reward}%`;
  if (rewardType === "fixed") return `£${reward}`;
  return String(reward);
};

const getNormalizedPointBased = (selectedPointBased) => {
  const pointBaseCard = selectedPointBased?.point_base_card || selectedPointBased || {};

  const customerName =
    selectedPointBased?.customer_full_name ||
    selectedPointBased?.customer?.full_name ||
    null;

  const previewCode = selectedPointBased?.code || pointBaseCard?.code || null;
  const rewardType = pointBaseCard?.type || selectedPointBased?.type;
  const rawReward = selectedPointBased?.reward ?? pointBaseCard?.reward ?? null;

  return {
    ...pointBaseCard,
    previewCode,
    previewCustomerName: customerName,
    previewRewardLabel: formatReward(rawReward, rewardType),
    previewUsedDate: formatDate(selectedPointBased?.used_at),
    previewUsedPoints: selectedPointBased?.used_points ?? null,
    previewRemainingPoints: selectedPointBased?.remaining_points ?? null,
    previewMinimumRequiredPoints:
      pointBaseCard?.minimum_required_points ?? selectedPointBased?.minimum_required_points ?? null,
    previewEligibleRewardPoints:
      pointBaseCard?.eligible_reward_points ?? selectedPointBased?.eligible_reward_points ?? null,
    previewMessage: pointBaseCard?.template_note || pointBaseCard?.note || null,
  };
};

const getPreviewData = ({ normalizedPointBased }) => ({
  strictTablePreview: true,
  title: normalizedPointBased?.note,
  customerName: normalizedPointBased?.previewCustomerName,
  code: normalizedPointBased?.previewCode,
  rewardLabel: normalizedPointBased?.previewRewardLabel,
  eligibleRewardPoints: normalizedPointBased?.previewEligibleRewardPoints,
  minimumRequiredPoints: normalizedPointBased?.previewMinimumRequiredPoints,
  usedPoints: normalizedPointBased?.previewUsedPoints,
  remainingPoints: normalizedPointBased?.previewRemainingPoints,
  usedDate: normalizedPointBased?.previewUsedDate,
  message: normalizedPointBased?.previewMessage,
  isConjunction: normalizedPointBased?.is_conjunction,
});

export const exportPointBasedTemplatePdf = async ({
  selectedPointBased,
  templates = [],
  fileName = "point-based-preview.pdf",
}) => {
  const normalizedPointBased = getNormalizedPointBased(selectedPointBased);
  const orderedTemplates = sortTemplatesBySerial(templates || []);
  const selectedTemplate =
    getTemplateForRecord(orderedTemplates, normalizedPointBased?.template_id) ||
    orderedTemplates[0] ||
    null;
  const SelectedTemplateComponent =
    getComponentByTemplate(selectedTemplate, POINT_TEMPLATE_COMPONENTS) ||
    POINT_TEMPLATE_COMPONENTS[0];
  const previewData = getPreviewData({ normalizedPointBased, selectedTemplate });

  const mountNode = document.createElement("div");
  mountNode.style.position = "fixed";
  mountNode.style.left = "0";
  mountNode.style.top = "0";
  mountNode.style.opacity = "0";
  mountNode.style.width = "546px";
  mountNode.style.zIndex = "-1";
  mountNode.style.pointerEvents = "none";
  mountNode.style.background = "#ffffff";
  document.body.appendChild(mountNode);

  const root = createRoot(mountNode);

  try {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);

    root.render(
      <div
        id="point-based-export-capture"
        style={{ width: "546px", background: "#ffffff" }}
      >
        <SelectedTemplateComponent template={selectedTemplate} previewData={previewData} />
      </div>,
    );

    await waitForRender();
    const renderRootNode = mountNode.querySelector("#point-based-export-capture");
    if (!renderRootNode) {
      throw new Error("Could not prepare point-based preview for export.");
    }

    await waitForFonts();
    await inlineAssetsForCapture(renderRootNode);
    await waitForImages(renderRootNode);

    const captureNode =
      renderRootNode.querySelector('[data-export-capture="point-based-template"]') ||
      renderRootNode;

    await waitForImages(captureNode);
    await waitForRender();

    const rect = captureNode.getBoundingClientRect();
    const pdfWidth = Math.max(1, Math.round(rect.width));
    const pdfHeight = Math.max(1, Math.round(rect.height));

    const captureScale = Math.min(5, Math.max(3, (window.devicePixelRatio || 1) * 2));

    const sourceCanvas = await html2canvas(captureNode, {
      scale: captureScale,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: pdfWidth,
      height: pdfHeight,
      imageTimeout: 0,
    });

    const imageData = sourceCanvas.toDataURL("image/png", 1.0);
    const pdf = new jsPDF({
      orientation: pdfWidth >= pdfHeight ? "landscape" : "portrait",
      unit: "px",
      format: [pdfWidth, pdfHeight],
    });

    pdf.addImage(imageData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(fileName);
  } finally {
    root.unmount();
    mountNode.remove();
  }
};